import React from "react";
import styled from "styled-components";
import {
  getImageMaxWidthUrl,
  // getPriceLabel,
  logEvent,
  getRedirectUrl
} from "../../utilities";

const Wrapper = styled.a`
  --label-height: 25px;
  --big-gutter: calc(var(--gutter) + var(--label-height));

  box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.2);
  color: currentColor;
  display: block;
  margin: var(--big-gutter) 0 var(--gutter);
  text-decoration: none;
  transition: all ease 200ms;
  width: 100%;

  &:hover {
    transform: scale(1.01);
    box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.2);
  }
`;

const MiniWrapper = styled(Wrapper)`
  margin-top: var(--gutter);
`;

const BigWrapper = styled(Wrapper)`
  --label-height: 40px;
`;

const Box = styled.div`
  --border-width: 2px;

  border: 2px solid #005bea; // IE
  border: var(--border-width) solid var(--category-main-color);
  border-image: linear-gradient(to right, #005bea, #00c6fb) 1; // IE
  border-image: var(--category-gradient) 1;
  display: grid;
  font-size: 14px;
  grid-template-areas:
    "title"
    "image"
    "cta";
  grid-gap: 0 var(--gutter);
  grid-template-rows: 0fr 1fr 0fr;
  height: 100%;
  line-height: 1.5;
  padding: 20px; // IE
  padding: var(--gutter);
  position: relative;
`;

const MiniBox = styled(Box)`
  grid-template-areas:
    "image"
    "title"
    "cta";
  grid-template-rows: 0fr auto 0fr;
  padding: calc(var(--gutter) / 2);
  border: 1px solid #ebebeb;
`;

const ThinBox = styled(Box)`
  grid-template-areas:
    "image"
    "title"
    "subtitle"
    "description"
    "cta";
  grid-template-rows: auto;

  @media (min-width: 500px) {
    grid-template-areas:
      "image title"
      "image subtitle"
      "image description"
      "image cta";
    grid-template-columns: 180px 1fr;
    grid-template-rows: 0fr 0fr 1fr 0fr;
  }
`;

const BigBox = styled(Box)`
  --border-width: 5px;

  grid-template-areas:
    "image"
    "title"
    "subtitle"
    "description"
    "cta";
  grid-template-rows: auto;

  @media (min-width: 500px) {
    grid-template-areas:
      "image title"
      "image subtitle"
      "image description"
      "image cta";
    grid-template-columns: 180px 1fr;
    grid-template-rows: 0fr 0fr 1fr 0fr;
  }
`;

const Label = styled.div`
  background: var(--category-gradient);
  bottom: 100%;
  color: #fff;
  font-family: var(--font-serif);
  font-size: 1.15em;
  font-style: italic;
  left: calc(var(--border-width) * -1);
  right: calc(var(--border-width) * -1);
  padding: 0 0 var(--border-width);
  position: absolute;
  text-align: center;
  display: grid;
  grid-template-columns: minmax(10px, 1fr) auto minmax(10px, 1fr);
  grid-gap: 10px;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &::before,
  &::after {
    content: "";
    width: 100%;
    display: block;
    background-color: #fff;
    transform: scale(1.1);
    transform-origin: right bottom;
  }

  &::after {
    transform-origin: left bottom;
  }
`;

const BigLabel = styled(Label)`
  font-size: 1.7em;
`;

const Img = styled.img`
  height: 180px;
  width: 100%;
  object-fit: contain;
  align-self: end;
  grid-area: image;
  margin-bottom: var(--gutter);
`;

const MiniImg = styled.img`
  height: 90px;
  width: 100%;
  object-fit: contain;
  align-self: end;
  grid-area: image;
  margin-bottom: var(--gutter);
`;

const ThinImg = styled.img`
  height: 140px;
  width: 100%;
  object-fit: contain;
  align-self: center;
  grid-area: image;

  @media (max-width: 499px) {
    margin-bottom: var(--gutter);
  }
`;

const BigImg = styled.img`
  float: left;
  margin-right: 20px;
  max-height: 240px;

  @supports (display: grid) {
    float: none;
    margin-right: 0;
    align-self: center;
    grid-area: image;
    justify-self: center;
  }

  @media (max-width: 499px) {
    margin-bottom: var(--gutter);
  }
`;

const Title = styled.div`
  color: var(--category-main-color);
  font-family: var(--font-serif);
  font-size: 1.5em;
  font-style: italic;
  font-weight: normal;
  line-height: 1.1;
  margin: 0 0 calc(var(--gutter) / 2);
  text-align: center;

  a {
    color: currentColor;
  }
`;

const MiniTitle = styled(Title)`
  font-size: 1.07em;
  font-family: var(--font-sans);
  font-style: normal;
  color: currentColor;
  font-weight: bold;
`;

const ThinTitle = styled(MiniTitle)`
  text-align: left;
`;

const BigTitle = styled(Title)`
  text-decoration: underline;
  font-size: 2em;
  text-align: left;
`;

const Subtitle = styled.p`
  font-size: 1.3em;
  font-weight: normal;
  margin: 0 0 calc(var(--gutter) / 2);
`;

const P = styled.p`
  margin: 0 0 calc(var(--gutter) / 2);
`;

const CTA = styled.div`
  background-color: #ff6b00; // IE
  background-color: var(--color-cta);
  border-radius: 5px;
  color: #fff;
  display: inline-block;
  font-size: 1em;
  font-weight: bold;
  line-height: 1.1;
  padding: 10px;
  text-align: center;
  text-decoration: none;
  transition: all ease 200ms;

  &:hover {
    opacity: 0.8;
  }

  small {
    font-size: 0.7em;
    font-weight: normal;
    margin-right: 10px;

    @supports (display: grid) {
      display: block;
      margin-right: 0;
    }
  }
`;

const BigCTA = styled(CTA)`
  font-size: 2em;
`;

function log(productName, scope = "product") {
  logEvent({
    category: scope,
    action: "product-click",
    label: productName
  });
}

const ProductMini = props => (
  <MiniWrapper
    target="_blank"
    rel="sponsored"
    href={getRedirectUrl(props.product.id)}
    data-label={props.product.label}
    className={props.className}
    onClick={() => log(props.product.title, props.scope)}
  >
    <MiniBox>
      <MiniImg
        src={getImageMaxWidthUrl(props.product.imageId, 180)}
        alt={props.product.title}
      />
      <MiniTitle>{props.product.title}</MiniTitle>
      {/* <CTA>
        {`${props.product.ctaLabel} ${getPriceLabel(
          parseFloat(props.product.price, 10)
        )}`}
      </CTA> */}
      <CTA>{props.product.ctaLabel}</CTA>
    </MiniBox>
  </MiniWrapper>
);

const ProductThin = props => (
  <Wrapper
    target="_blank"
    rel="sponsored"
    href={getRedirectUrl(props.product.id)}
    data-label={props.product.label}
    className={props.className}
    onClick={() => log(props.product.title, props.scope)}
  >
    <ThinBox>
      <Label>
        <span>{props.product.label}</span>
      </Label>
      <ThinImg
        src={getImageMaxWidthUrl(props.product.imageId, 180)}
        alt={props.product.title}
      />
      <ThinTitle>{props.product.title}</ThinTitle>
      <Subtitle>{props.product.brand}</Subtitle>
      <P>{props.product.description}</P>
      {/* <CTA>
        {`${props.product.ctaLabel} ${getPriceLabel(
          parseFloat(props.product.price, 10)
        )}`}
      </CTA> */}
      <CTA>{props.product.ctaLabel}</CTA>
    </ThinBox>
  </Wrapper>
);

const ProductSmall = props => (
  <Wrapper
    target="_blank"
    rel="sponsored"
    href={getRedirectUrl(props.product.id)}
    data-label={props.product.label}
    className={props.className}
    onClick={() => log(props.product.title, props.scope)}
  >
    <Box>
      <Label>
        <span>{props.product.label}</span>
      </Label>
      <Img
        src={getImageMaxWidthUrl(props.product.imageId, 180)}
        alt={props.product.title}
      />
      <Title>{props.product.title}</Title>
      {/* <CTA>
        {`${props.product.ctaLabel} ${getPriceLabel(
          parseFloat(props.product.price, 10)
        )}`}
      </CTA> */}
      <CTA>{props.product.ctaLabel}</CTA>
    </Box>
  </Wrapper>
);

const Product = props => (
  <BigWrapper
    target="_blank"
    rel="sponsored"
    href={getRedirectUrl(props.product.id)}
    data-label={props.product.label}
    className={props.className}
    onClick={() => log(props.product.title, props.scope)}
  >
    <BigBox>
      <BigLabel>
        <span>{props.product.label}</span>
      </BigLabel>
      <BigImg
        src={getImageMaxWidthUrl(props.product.imageId, 180)}
        alt={props.product.title}
      />
      <BigTitle>{props.product.title}</BigTitle>
      <Subtitle>{props.product.brand}</Subtitle>
      <P>{props.product.description}</P>
      <BigCTA>
        <small>{props.product.ctaLabel}</small>
        {/* {getPriceLabel(parseFloat(props.product.price, 10))} */}
      </BigCTA>
    </BigBox>
  </BigWrapper>
);

export default Product;
export { ProductSmall, ProductMini, ProductThin };
